import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

const INDUSTRIES = [
  "Food Manufacturing",
  "Hospitality / F&B",
  "Distribution & Warehousing",
  "Import / Export",
  "Other SME (Scaling Operations)",
];

const COMPANY_SIZES = [
  "1–10 employees",
  "11–50 employees",
  "51–200 employees",
  "201–500 employees",
  "500+ employees",
];

const COMPLIANCE_STAGES = [
  "Just exploring food compliance",
  "Preparing for our first food compliance audit",
  "Mid-certification",
  "Already certified — maintaining readiness",
  "Scaling operations / not food compliance yet",
];

const INITIAL = {
  name: "",
  work_email: "",
  company: "",
  industry: "",
  company_size: "",
  phone: "",
  compliance_stage: "",
  challenge: "",
};

export function EnquiryForm({ formId = "enquiry-form" }) {
  const [values, setValues] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const utmRef = useRef({});

  useEffect(() => {
    // Capture UTM + referrer once when the form mounts. Persist to sessionStorage
    // so it survives navigation across the landing page without being lost.
    const params = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const captured = {};
    keys.forEach((k) => {
      const v = params.get(k);
      if (v) captured[k] = v.slice(0, 200);
    });

    let stored = {};
    try { stored = JSON.parse(sessionStorage.getItem("vb_utm") || "{}"); } catch (_) { /* ignore */ }
    const merged = { ...stored, ...captured };
    if (document.referrer && !merged.referrer) merged.referrer = document.referrer.slice(0, 500);
    try { sessionStorage.setItem("vb_utm", JSON.stringify(merged)); } catch (_) { /* ignore */ }
    utmRef.current = merged;
  }, []);

  const update = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (!values.name.trim()) e.name = "Name is required.";
    if (!values.work_email.trim()) e.work_email = "Work email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.work_email)) e.work_email = "Enter a valid email.";
    if (!values.company.trim()) e.company = "Company is required.";
    if (!values.industry) e.industry = "Please select an industry.";
    if (!values.company_size) e.company_size = "Please select a company size.";
    if (!values.phone.trim() || values.phone.trim().length < 6) e.phone = "Phone number is required.";
    if (!values.compliance_stage) e.compliance_stage = "Please select your current stage.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        challenge: values.challenge?.trim() || null,
        ...utmRef.current,
      };
      const { data } = await api.post("/enquiries", payload);
      setSubmitted(true);
      toast.success(data.message || "Enquiry sent — we'll be in touch.");
      setValues(INITIAL);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="bg-white text-brand-navy p-8 md:p-12 border border-brand-navy/10 flex flex-col items-start rounded-3xl shadow-sm"
        data-testid="enquiry-success"
      >
        <CheckCircle2 size={36} className="text-brand-orange" />
        <h3 className="mt-4 text-2xl font-semibold">Thanks — we've got your enquiry.</h3>
        <p className="mt-2 text-brand-slate/80">
          A member of our team will reach out within 1 business day to schedule your free
          30-minute Operational Review.
        </p>
        <button
          type="button"
          className="mt-6 text-sm font-semibold text-brand-orange hover:text-brand-orange-hover"
          onClick={() => setSubmitted(false)}
          data-testid="enquiry-submit-another"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form id={formId} onSubmit={onSubmit} className="bg-white text-brand-navy p-7 md:p-12 rounded-3xl border border-brand-navy/10 shadow-sm" data-testid="enquiry-form" noValidate>
      <h3 className="text-2xl md:text-3xl font-semibold leading-tight">Book your free Operational Review</h3>
      <p className="mt-2 text-sm md:text-base text-brand-slate/80">
        Tell us a little about your operations. We'll respond within 1 business day.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <Field id="name" label="Full name" required error={errors.name}>
          <Input
            id="name"
            value={values.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder=""
            data-testid="enquiry-input-name"
            className="rounded-xl h-11"
          />
        </Field>
        <Field id="work_email" label="Work email" required error={errors.work_email}>
          <Input
            id="work_email"
            type="email"
            value={values.work_email}
            onChange={(e) => update("work_email", e.target.value)}
            placeholder=""
            data-testid="enquiry-input-email"
            className="rounded-xl h-11"
          />
        </Field>
        <Field id="company" label="Company" required error={errors.company}>
          <Input
            id="company"
            value={values.company}
            onChange={(e) => update("company", e.target.value)}
            placeholder=""
            data-testid="enquiry-input-company"
            className="rounded-xl h-11"
          />
        </Field>
        <Field id="phone" label="Phone (required)" required error={errors.phone}>
          <Input
            id="phone"
            type="tel"
            value={values.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder=""
            data-testid="enquiry-input-phone"
            className="rounded-xl h-11"
          />
        </Field>
        <Field id="industry" label="Industry" required error={errors.industry}>
          <NativeSelect
            id="industry"
            value={values.industry}
            onChange={(v) => update("industry", v)}
            placeholder="Select an industry"
            options={INDUSTRIES}
            testid="enquiry-select-industry"
          />
        </Field>
        <Field id="company_size" label="Company size" required error={errors.company_size}>
          <NativeSelect
            id="company_size"
            value={values.company_size}
            onChange={(v) => update("company_size", v)}
            placeholder="Select company size"
            options={COMPANY_SIZES}
            testid="enquiry-select-size"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field id="compliance_stage" label="Current compliance stage" required error={errors.compliance_stage}>
            <NativeSelect
              id="compliance_stage"
              value={values.compliance_stage}
              onChange={(v) => update("compliance_stage", v)}
              placeholder="Where are you in your food compliance journey?"
              options={COMPLIANCE_STAGES}
              testid="enquiry-select-stage"
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field id="challenge" label="Biggest operational challenge (optional)">
            <Textarea
              id="challenge"
              value={values.challenge}
              onChange={(e) => update("challenge", e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder=""
              data-testid="enquiry-input-challenge"
              className="rounded-xl resize-none"
            />
          </Field>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-8 w-full sm:w-auto inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 font-semibold hover:bg-brand-orange-hover transition-colors rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="enquiry-submit-button"
      >
        {submitting ? "Sending…" : "Send Enquiry"}
      </button>
      <p className="mt-4 text-xs text-brand-slate/60">
        By submitting, you agree to be contacted by Vanguard Beacon regarding your operational review. We never share your data.
      </p>
    </form>
  );
}

function Field({ id, label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-brand-navy">
        {label} {required && <span className="text-brand-orange">*</span>}
      </Label>
      {children}
      {error && (
        <span className="text-xs text-red-600" data-testid={`enquiry-error-${id}`}>
          {error}
        </span>
      )}
    </div>
  );
}

function NativeSelect({ id, value, onChange, placeholder, options, testid }) {
  // Native select for reliability and a11y; styled to match.
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none focus:ring-0"
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}
